/** Запись голоса в браузере.
 *
 * Работает так же, как голос в чате: запись уходит в тот же `handle_voice`,
 * который обслуживает бота, — Whisper, дедуп, разбор фразы. Отличается только
 * способ нажатия: в Telegram это удержание, здесь — тап на старт и тап на стоп.
 *
 * Тап, а не удержание, намеренно: первый раз браузер спрашивает разрешение на
 * микрофон, и диалог прерывает удержание — палец отпущен, запись не началась,
 * человек не понял почему. С тапом разрешение спрашивается один раз и не мешает.
 */
export type VoiceSupport = "ready" | "unsupported";

export function voiceSupport(): VoiceSupport {
  const ok =
    typeof MediaRecorder !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;
  return ok ? "ready" : "unsupported";
}

/** Формат выбираем из того, что умеет браузер: Safari пишет в mp4, Chrome в webm. */
function pickMime(): string | undefined {
  const wanted = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  return wanted.find((type) => MediaRecorder.isTypeSupported?.(type));
}

export type Recording = {
  /** Останавливает запись и отдаёт результат. */
  stop: () => Promise<Blob>;
  /** Бросает запись: микрофон освобождается, ничего не возвращается. */
  cancel: () => void;
};

export async function startRecording(): Promise<Recording> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = pickMime();
  const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks: BlobPart[] = [];
  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
  recorder.start();

  // Микрофон отпускаем в любом случае — иначе индикатор записи остаётся гореть,
  // и человек справедливо решает, что его продолжают слушать.
  const release = () => stream.getTracks().forEach((t) => t.stop());

  return {
    stop: () =>
      new Promise<Blob>((resolve) => {
        recorder.onstop = () => {
          release();
          resolve(new Blob(chunks, { type: recorder.mimeType || mime || "audio/webm" }));
        };
        recorder.stop();
      }),
    cancel: () => {
      recorder.onstop = release;
      if (recorder.state !== "inactive") recorder.stop();
      else release();
    },
  };
}
